import type { Rectangle2d, Point2d } from "@logic/lib/2d"
import { isPointInBox, doSegmentsIntersect, boxToEdges } from "@logic/lib/2d"
import type { RoofEdge, Panel } from "@logic/lib/design"
import { studioToScreen } from "@logic/lib/design"
import type { Edge2d, Edge2dComputed } from "@logic/lib/mesh"
import type { RecoilState } from "recoil"

import type { RecoilStateFamily } from "~/components/ui"

import type { TransactionOperation } from ".."
import { zoomState } from "../../../view"

export type GatherPointsWithinBoxOptions = {
  box: Rectangle2d
  pointStore: [RecoilStateFamily<Point2d, string>, RecoilState<string[]>]
}

export const gatherPointsWithinBox: TransactionOperation<
  GatherPointsWithinBoxOptions,
  string[]
> = (transactors, { box, pointStore }) => {
  const { get } = transactors
  const [findPointState, pointIndex] = pointStore
  const pointIds = get(pointIndex)
  const gathered = pointIds.filter((id) => {
    const node = get(findPointState(id))
    const zoom = get(zoomState)
    const projectedPoint = studioToScreen.point(node, zoom)

    return isPointInBox(projectedPoint, box)
  })

  return gathered
}

export type gatherEdgesWithinBoxOptions = {
  box: Rectangle2d
  edgeStore:
    | [RecoilStateFamily<Edge2d, string>, RecoilState<string[]>]
    | [RecoilStateFamily<RoofEdge, string>, RecoilState<string[]>]
  findPointState: RecoilStateFamily<Point2d, string>
}

export const doesEdgeIntersectBox = (
  edge: Edge2dComputed,
  box: Rectangle2d,
): boolean => {
  const { x: bx, y: by, h: bh, w: bw } = box
  const boxNodes: Point2d[] = [
    { x: bx, y: by },
    { x: bx + bw, y: by },
    { x: bx + bw, y: by + bh },
    { x: bx, y: by + bh },
  ]
  const boxEdges: Edge2dComputed[] = [
    { p1: boxNodes[0], p2: boxNodes[1] },
    { p1: boxNodes[1], p2: boxNodes[2] },
    { p1: boxNodes[2], p2: boxNodes[3] },
    { p1: boxNodes[3], p2: boxNodes[0] },
  ]
  if (boxEdges.some((p) => doSegmentsIntersect(edge, p))) {
    return true
  }
  const { p1, p2 } = edge

  return [p1, p2].some((p) => isPointInBox(p, box))
}

export const gatherEdgesWithinBox: TransactionOperation<
  gatherEdgesWithinBoxOptions,
  string[]
> = (transactors, { box, edgeStore, findPointState }) => {
  const { get } = transactors
  const [findEdgeState, edgeIndex] = edgeStore
  const edgeIds = get(edgeIndex)
  const gathered = edgeIds.filter((id) => {
    const edge = get(findEdgeState(id))
    const { node1id, node2id } = edge
    const zoom = get(zoomState)
    const p1 = studioToScreen.point(get(findPointState(node1id)), zoom)
    const p2 = studioToScreen.point(get(findPointState(node2id)), zoom)

    return doesEdgeIntersectBox({ p1, p2 }, box)
  })

  return gathered
}

export const doRectanglesCollide = (a: Rectangle2d, b: Rectangle2d): boolean => {
  const aSegments = boxToEdges(a)
  const bSegments = boxToEdges(b)
  if (
    aSegments.some((aSegment) =>
      bSegments.some((bSegment) => doSegmentsIntersect(aSegment, bSegment)),
    )
  ) {
    return true
  }
  for (const { p1, p2 } of aSegments) {
    if (isPointInBox(p1, b) || isPointInBox(p2, b)) {
      return true
    }
  }

  return false
}

export type GatherRectanglesWithinBoxOptions = {
  box: Rectangle2d
  rectangleStore: [
    RecoilStateFamily<Panel, string> | RecoilStateFamily<Rectangle2d, string>,
    RecoilState<string[]>,
  ]
}

export const gatherRectanglesWithinBox: TransactionOperation<
  GatherRectanglesWithinBoxOptions,
  string[]
> = (transactors, { box, rectangleStore }) => {
  const { get } = transactors
  const [findRectangleState, rectangleIndex] = rectangleStore
  const rectangleIds = get(rectangleIndex)
  const zoom = get(zoomState)
  const gathered = rectangleIds.filter((id) => {
    const rectangle = get(findRectangleState(id))
    const onScreenRectangle = studioToScreen.rectangle(rectangle, zoom)

    return doRectanglesCollide(onScreenRectangle, box)
  })

  return gathered
}
